// @ts-nocheck
// Pure derivations for the team-lead dashboard. Everything here maps the
// real `team-lead-real-time` SSE payload — groups[].agents[] of
// { agent, user, dashboard } — and the `team-lead-stats` rows onto the
// display model the team components render. No React, no fetching.
export { formatClock as clock } from '../utils/format';

export const AGENT_STATES = {
  onCall: { key: 'onCall', label: 'On call', tone: 'call' },
  available: { key: 'available', label: 'Available', tone: 'good' },
  break: { key: 'break', label: 'On break', tone: 'warn', dim: true },
  offline: { key: 'offline', label: 'Offline', tone: 'off' },
};

export const STATE_ORDER = ['onCall', 'available', 'break', 'offline'];

export function connectTone(pct) {
  if (pct == null) return 'off';
  if (pct >= 72) return 'good';
  if (pct >= 62) return 'warn';
  return 'crit';
}

export function agentStateKey(agent, user) {
  if (Number(agent?.call_status) === 1) return 'onCall';
  if (user?.break_id) return 'break';
  if (agent?.login_status === '1') return 'available';
  return 'offline';
}

export function activityLabel(dashboard) {
  const raw = dashboard?.status;
  if (!raw) return null;
  const s = String(raw);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ');
}

export function activeExtension(agent) {
  const profiles = agent?.agentTelephony?.profile || [];
  const active = profiles.find((p) => p.active_profile === true);
  return active?.number?.number || null;
}

export function breakRemaining(user, nowSeconds) {
  if (!user?.break_id || !user?.breakDetail || !user?.updated_at) return null;
  const total = Number(user.breakDetail.duration || 0) * 60;
  const startedAt = Math.floor(new Date(user.updated_at).getTime() / 1000);
  if (!startedAt) return null;
  const remaining = total - (nowSeconds - startedAt);
  return { title: user.breakDetail.title, remaining, exceeded: remaining <= 0 };
}

export function snoopableCall(dashboard) {
  const answered = String(dashboard?.callStatus || '').toUpperCase() === 'ANSWERED';
  return { answered, callId: dashboard?.agentCallId || null, ready: answered && Boolean(dashboard?.agentCallId) };
}

/** Flatten the SSE groups into one decorated row list plus a per-group view. */
export function buildRoster(groups, statsRows, nowSeconds) {
  const statsById = new Map(
    (statsRows || []).map((row) => [String(row.agent_id), row]),
  );

  const decorate = (item, groupName, groupId) => {
    const agent = item?.agent || {};
    const user = item?.user || {};
    const dashboard = item?.dashboard || {};
    const stateKey = agentStateKey(agent, user);
    const stats = statsById.get(String(agent.id)) || null;
    const total = Number(stats?.total_calls) || 0;
    return {
      id: agent.id,
      raw: item,
      agent,
      user,
      dashboard,
      name: agent.agent_name || '-',
      number: agent.agent_number || null,
      extension: activeExtension(agent),
      stateKey,
      state: AGENT_STATES[stateKey],
      activity: activityLabel(dashboard),
      snoop: snoopableCall(dashboard),
      breakInfo: breakRemaining(user, nowSeconds),
      groupName,
      groupId,
      stats,
      calls: total,
      connectPct: total > 0 ? Math.round(((Number(stats?.connected) || 0) / total) * 100) : null,
    };
  };

  const decoratedGroups = (groups || []).map((g) => ({
    id: g.call_group_id,
    name: g.call_group_name || 'Ungrouped',
    agents: (g.agents || []).map((item) => decorate(item, g.call_group_name, g.call_group_id)),
  }));

  const seen = new Set();
  const allAgents = [];
  decoratedGroups.forEach((g) =>
    g.agents.forEach((a) => {
      const key = String(a.id ?? `${g.id}:${a.name}`);
      if (seen.has(key)) return;
      seen.add(key);
      allAgents.push(a);
    }),
  );

  return { groups: decoratedGroups, allAgents };
}

export function rosterCounts(allAgents) {
  const by = (key) => allAgents.filter((a) => a.stateKey === key).length;
  return {
    total: allAgents.length,
    onCall: by('onCall'),
    available: by('available'),
    onBreak: by('break'),
    offline: by('offline'),
    online: allAgents.length - by('offline'),
  };
}

export function statsTotals(statsRows) {
  const sum = (key) => (statsRows || []).reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
  return {
    inbound: sum('inbound'),
    outbound: sum('outbound'),
    total: sum('total_calls'),
    connected: sum('connected'),
    missed: sum('missed'),
  };
}

const LOW_CONNECT_PCT = 60;

/** Only exceptions the live payload can actually prove — no invented SLAs. */
export function rosterAlerts(allAgents) {
  const overBreak = allAgents.filter(
    (a) => a.breakInfo && a.breakInfo.remaining <= 0,
  );
  const lowConnect = allAgents.filter(
    (a) => a.connectPct != null && a.calls > 0 && a.connectPct < LOW_CONNECT_PCT,
  );
  const noProfile = allAgents.filter((a) => a.stateKey !== 'offline' && !a.extension);

  return [
    { key: 'break', tone: 'crit', n: overBreak.length, text: 'over their break time', who: overBreak },
    { key: 'connect', tone: 'warn', n: lowConnect.length, text: `below a ${LOW_CONNECT_PCT}% connect rate`, who: lowConnect },
    { key: 'profile', tone: 'warn', n: noProfile.length, text: 'logged in with no active profile', who: noProfile },
  ].filter((a) => a.n > 0);
}

/** SSE `queue.queueData` is [{ key: 'a:b:c:<queueName>', value: { <id>: call } }]. */
export function buildQueues(queue) {
  const rows = Array.isArray(queue?.queueData) ? queue.queueData : [];
  return rows
    .filter((q) => q?.value && Object.keys(q.value).length > 0)
    .map((q) => ({
      name: String(q.key || '').split(':')[3] || String(q.key || '—'),
      calls: Object.values(q.value),
    }));
}

/** Server's own waiting count, falling back to the per-queue call lists. */
export function queueWaiting(queue) {
  const reported = Number(queue?.count);
  if (Number.isFinite(reported) && reported > 0) return reported;
  return buildQueues(queue).reduce((acc, q) => acc + q.calls.length, 0);
}
